import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, FilePlus2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { formatNumber } from '@/utils/format'
import { useGenerateInvoices, usePreviewGeneration, type GenerateInput } from '../api'
import { generateSchema, type GenerateValues } from '../schemas'
import { frequencyLabel, localizePeriodLabel } from '../status'
import type { GenerationResult } from '../types'
import { useMoney } from '../useCurrency'
import { ToggleField } from './ToggleField'

const FORM_ID = 'generate-invoices-form'
const FIELDS = ['academicYearId', 'classId', 'month', 'issueDate', 'dueDate', 'includeOptional'] as const

function thisMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function toInput(v: GenerateValues): GenerateInput {
  return {
    academicYearId: Number(v.academicYearId),
    classId: v.classId ? Number(v.classId) : null,
    period: v.periodType === 'annual' ? 'annual' : v.month,
    issueDate: v.issueDate || null,
    dueDate: v.dueDate || null,
    includeOptional: v.includeOptional,
  }
}

/**
 * Three-step wizard: choose class / year / period → dry-run preview (nothing written) → confirm.
 * Generation is idempotent, so re-running a period only bills what is missing.
 */
export function GenerateInvoicesDialog({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone?: (r: GenerationResult) => void
}) {
  const { t } = useTranslation('finance')
  const years = useQuery(yearsQuery())
  const classes = useQuery(classesQuery())
  const preview = usePreviewGeneration()
  const generate = useGenerateInvoices()
  const { money } = useMoney()
  const [previewed, setPreviewed] = useState<{ input: GenerateInput; result: GenerationResult } | null>(null)
  const [done, setDone] = useState<GenerationResult | null>(null)
  const currentYearId = years.data?.find((y) => y.isCurrent)?.id

  const {
    register,
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<GenerateValues>({
    resolver: zodResolver(generateSchema),
    values: {
      academicYearId: currentYearId ? String(currentYearId) : '',
      classId: '',
      periodType: 'month',
      month: thisMonth(),
      issueDate: '',
      dueDate: '',
      includeOptional: false,
    },
    resetOptions: { keepDirtyValues: true },
  })
  const periodType = watch('periodType')

  const submitPreview = handleSubmit(async (v) => {
    try {
      const input = toInput(v)
      setPreviewed({ input, result: await preview.mutateAsync(input) })
    } catch (e) {
      applyServerErrors(e, setError, [...FIELDS, 'period'])
    }
  })

  const confirm = async () => {
    if (!previewed) return
    try {
      const result = await generate.mutateAsync(previewed.input)
      setDone(result)
      toast.success(
        t('Invoices generated'),
        t('{{count}} invoices for {{period}}', {
          count: result.invoiceCount,
          formatted: formatNumber(result.invoiceCount),
          period: localizePeriodLabel(result.periodLabel),
        }),
      )
      onDone?.(result)
    } catch (e) {
      toast.error(t('Could not generate invoices'), e instanceof Error ? e.message : undefined)
    }
  }

  const pending = preview.isPending || generate.isPending
  const r = done ?? previewed?.result

  let footer
  if (done) {
    footer = <Button onClick={() => onOpenChange(false)}>{t('Done')}</Button>
  } else if (previewed) {
    footer = (
      <>
        <Button variant="secondary" onClick={() => setPreviewed(null)} disabled={pending}>
          {t('Back')}
        </Button>
        <Button
          onClick={() => void confirm()}
          loading={generate.isPending}
          disabled={previewed.result.invoiceCount === 0}
        >
          {previewed.result.invoiceCount === 0
            ? t('Nothing to generate')
            : t('Generate {{count}} invoices', {
                count: previewed.result.invoiceCount,
                formatted: formatNumber(previewed.result.invoiceCount),
              })}
        </Button>
      </>
    )
  } else {
    footer = (
      <>
        <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
          {t('Cancel')}
        </Button>
        <Button type="submit" form={FORM_ID} loading={preview.isPending}>
          {t('Preview')}
        </Button>
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<FilePlus2 />}
      title={t('Generate invoices')}
      description={
        done
          ? t('Generation finished.')
          : previewed
            ? t('Step 2 of 2 · Review before anything is billed.')
            : t('Step 1 of 2 · Choose who and what to bill.')
      }
      size="lg"
      dismissible={!pending}
      footer={footer}
    >
      {!previewed && !done ? (
        <form
          id={FORM_ID}
          onSubmit={(e) => void submitPreview(e)}
          noValidate
          className="grid gap-4 sm:grid-cols-2"
        >
          <FormField label={t('Academic year')} error={errors.academicYearId?.message} required>
            <Select
              placeholder={t('Choose…')}
              options={(years.data ?? []).map((y) => ({
                value: y.id,
                label: y.isCurrent ? t('{{name}} (current)', { name: y.name }) : y.name,
              }))}
              {...register('academicYearId')}
            />
          </FormField>
          <FormField label={t('Class')} error={errors.classId?.message}>
            <Select
              placeholder={t('All classes')}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              {...register('classId')}
            />
          </FormField>
          <div className="sm:col-span-2">
            <p id="period-type-label" className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">
              {t('Billing period')}
            </p>
            <Controller
              control={control}
              name="periodType"
              render={({ field }) => (
                <RadioGroup
                  aria-labelledby="period-type-label"
                  appearance="cards"
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    {
                      value: 'month',
                      label: t('A month'),
                      description: t('Monthly fees, plus per-term fees in a term’s first month.'),
                    },
                    {
                      value: 'annual',
                      label: t('Annual'),
                      description: t('Yearly and one-time fees for the academic year.'),
                    },
                  ]}
                />
              )}
            />
          </div>
          {periodType === 'month' ? (
            <FormField label={t('Month')} error={errors.month?.message} required>
              <Input type="month" {...register('month')} />
            </FormField>
          ) : null}
          <FormField label={t('Issue date')} error={errors.issueDate?.message} hint={t('Defaults to today.')}>
            <Input type="date" {...register('issueDate')} />
          </FormField>
          <FormField
            label={t('Due date')}
            error={errors.dueDate?.message}
            hint={
              periodType === 'month'
                ? t('Defaults to the fee items’ due day.')
                : t('Defaults to 30 days after issue.')
            }
          >
            <Input type="date" {...register('dueDate')} />
          </FormField>
          <Controller
            control={control}
            name="includeOptional"
            render={({ field }) => (
              <ToggleField
                className="rounded-control border border-line bg-surface-muted/60 p-3 sm:col-span-2"
                label={t('Include optional fee items')}
                description={t('E.g. transport. Leave unchecked to bill mandatory fees only.')}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </form>
      ) : null}

      {r ? (
        <div className="space-y-4">
          {done ? (
            <Alert
              tone="success"
              title={t('{{count}} invoices created', {
                count: done.invoiceCount,
                formatted: formatNumber(done.invoiceCount),
              })}
            >
              {t('{{period}} · {{class}} · {{amount}} billed.', {
                period: localizePeriodLabel(done.periodLabel),
                class: done.schoolClass?.name ?? t('All classes'),
                amount: money(done.total),
              })}
            </Alert>
          ) : r.invoiceCount === 0 ? (
            <Alert tone="info" title={t('Nothing new to bill')}>
              {t(
                'Every eligible student already has these fees for {{period}}, or no active fee item applies to this period.',
                { period: localizePeriodLabel(r.periodLabel) },
              )}
            </Alert>
          ) : null}
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { key: 'students', label: t('Enrolled students'), value: formatNumber(r.studentCount) },
              {
                key: 'invoices',
                label: done ? t('Invoices created') : t('Invoices to create'),
                value: formatNumber(r.invoiceCount),
              },
              { key: 'billed', label: t('Already billed'), value: formatNumber(r.alreadyBilledCount) },
              { key: 'total', label: t('Total'), value: money(r.total) },
            ].map((s) => (
              <div key={s.key} className="rounded-control border border-line bg-surface-muted/60 p-3">
                <dt className="text-xs text-muted">{s.label}</dt>
                <dd className="mt-1 text-lg font-semibold text-ink tabular">{s.value}</dd>
              </div>
            ))}
          </dl>
          {r.noItemsCount > 0 ? (
            <p className="text-xs text-muted">
              {t('{{count}} students have no fee item for this period.', {
                count: r.noItemsCount,
                formatted: formatNumber(r.noItemsCount),
              })}
            </p>
          ) : null}
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('Fee items billed · {{period}}', { period: localizePeriodLabel(r.periodLabel) })}{' '}
              {!done ? <CheckCircle2 className="inline size-4 text-success-600" aria-hidden="true" /> : null}
            </h3>
            {r.items.length === 0 ? (
              <p className="text-sm text-muted">{t('No active fee item applies to this period.')}</p>
            ) : (
              <ul className="divide-y divide-line rounded-control border border-line">
                {r.items.map((i) => (
                  <li key={i.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-ink">{i.name}</span>
                    <Badge size="sm">{t(frequencyLabel[i.frequency])}</Badge>
                    <span className="hidden text-xs text-muted sm:inline">
                      {i.className ?? t('All classes')}
                    </span>
                    <span className="w-24 text-right font-medium tabular">{money(i.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </Dialog>
  )
}
